import { Box, type BoxProps } from "@mui/material";
import { type FC, memo, useCallback, useMemo } from "react";
import { Carousel, type CarouselItemProps } from "../discover/carousel/carousel";
import { CommsTemplateCarouselImage } from "./comms-template-carousel-image";
import { type CommsImageLoaderState, useGenerateCommsContext } from "./generate-comms-context";
import { useTemplateImageDimensions } from "./use-comms-template-image-dimensions";

export const CommsTemplateImages: FC<Omit<BoxProps, "onSelect">> = ({ ...props }) => {
  const {
    previewImages: { images, selectedImage, setSelectedImage },
    branding: { selectedImageType },
    numImages,
  } = useGenerateCommsContext();

  const dimensions = useTemplateImageDimensions(selectedImageType);
  const ratio = dimensions.width / dimensions.height;
  const height = 111;
  const width = height * ratio;

  const templateImages = useMemo(
    () =>
      !images || images.length === 0
        ? Array.from({ length: numImages }, () => ({ loading: true }) as CommsImageLoaderState)
        : images,
    [images, numImages],
  );

  const filteredTemplateImages = useMemo(() => {
    return templateImages.filter((image) => image !== undefined);
  }, [templateImages]);

  const getImageIndex = useCallback(
    (i: number) => {
      return templateImages.findIndex((x) => x === filteredTemplateImages[i]);
    },
    [templateImages, filteredTemplateImages],
  );

  return (
    <Box {...props}>
      <Carousel
        items={filteredTemplateImages.map((image, i) => ({
          ...image,
          key: i.toString(),
        }))}
        Item={Item}
        ItemExtraProps={{
          onSelect: (i: number) => {
            setSelectedImage(getImageIndex(i));
          },
          selected: (i: number) => selectedImage === getImageIndex(i),
          opacity: (i: number) => (selectedImage === getImageIndex(i) ? 1 : 0.8),
          width: `${width}px`,
          height: `${height}px`,
        }}
        itemWidth={`${width}px`}
        gap={2}
        sx={(theme) => ({
          "--carousel-bgColor": theme.palette.grey[100],
        })}
      />
    </Box>
  );
};

interface ExtraProps {
  onSelect?: (n: number) => void;
  selected: (i: number) => boolean;
  opacity: (i: number) => number;
  width: string;
  height: string;
}

const Item = memo(function ItemFn({
  value: image,
  i,
  extraProps: { onSelect, selected, opacity, width, height },
  ...props
}: CarouselItemProps<CommsImageLoaderState, ExtraProps>) {
  return (
    <CommsTemplateCarouselImage
      key={`img-${i}`}
      image={image}
      selected={selected(i)}
      onClick={() => onSelect?.(i)}
      sx={{
        opacity: opacity(i),
        width,
        height,
        ":hover": {
          opacity: 1,
        },
      }}
      {...props}
    />
  );
});
